// CONTRACT:C2-RENDER-ENGINE.1.0
/** SMEAR OVERLAP DETUNE — the pure math that stops overlapping uncertainty clouds
 *  from AMPLIFYING into a solid blob (render-polish: "smear opacity detune by overlap").
 *
 *  WHAT: the Gaussian uncertainty smear (apps/cop/src/host/cloudLayer.ts
 *  GaussianCloudLayer) composites additively, so where many clouds pile up they stack
 *  into a hard, bright thicket instead of a calm haze. This computes, per cloud, the
 *  FRACTION of its footprint covered by its neighbours and returns an opacity MULTIPLIER
 *  that recedes as overlap grows: a heavily-overlapped cloud dims so the iconography on
 *  top stays emphasized. ~90% overlap -> ~10% opacity (opacity ~= 1 - overlapFraction).
 *
 *  COVERAGE MODEL (deterministic, monotonic):
 *   - pairwise: the fraction of cloud i's disc area covered by its intersection with
 *     cloud j (circle-circle intersection area / area_i), in [0,1].
 *   - aggregate over all neighbours via a PROBABILISTIC UNION (1 - prod(1 - f_j)) so the
 *     coverage stays in [0,1] and is monotonic-increasing in every pairwise fraction
 *     (more / closer / bigger neighbours never RAISE opacity). No double-counting blow-up.
 *   - opacity = clamp(1 - coverage, floor, 1): monotonic-DECREASING in overlap.
 *
 *  CHANNEL LAW (unchanged): this scales the smear's ALPHA only (an opacity multiplier the
 *  host folds into getPeak). It NEVER changes reach/sigma (CONFIDENCE) and NEVER changes
 *  WHAT renders — the cloud is decoration behind a still-present, still-pickable marker.
 *
 *  PURE: imports nothing, deterministic, no GPU. The discs are passed in a single
 *  consistent planar unit (the host projects lng/lat + reach into local METRES). Tested in
 *  packages/render-engine/test/smearOverlap.test.ts. */

/** One uncertainty cloud as a disc in a shared planar unit (metres, in the host). */
export interface SmearDisc {
  /** Disc centre X (same unit as r). */
  x: number;
  /** Disc centre Y (same unit as r). */
  y: number;
  /** Disc radius = the cloud's reach. <=0 / non-finite is treated as no footprint. */
  r: number;
}

/** Tuning for {@link smearOpacityMultipliers}. */
export interface SmearOverlapOptions {
  /** Lowest opacity a fully-buried cloud recedes to (never fully vanishes). Default 0.05. */
  floor?: number;
}

const DEFAULT_FLOOR = 0.05;

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

/** Area of the lens where two circles (radii r1, r2, centre distance d) intersect.
 *  The standard closed form, total on the degenerate cases:
 *   - d >= r1+r2          -> disjoint, 0
 *   - d <= |r1-r2|        -> one disc fully inside the other, pi*min(r)^2
 *   - otherwise           -> the two circular segments. */
export function circleIntersectionArea(r1: number, r2: number, d: number): number {
  const a = Math.max(0, r1);
  const b = Math.max(0, r2);
  if (a === 0 || b === 0) return 0;
  const dist = Math.max(0, d);
  if (dist >= a + b) return 0; // disjoint
  if (dist <= Math.abs(a - b)) {
    const rMin = Math.min(a, b);
    return Math.PI * rMin * rMin; // smaller disc fully contained
  }
  const a2 = a * a;
  const b2 = b * b;
  const d2 = dist * dist;
  // angles subtended at each centre, clamped to acos' domain against FP drift.
  const alpha = Math.acos(clamp(-1, 1, (d2 + a2 - b2) / (2 * dist * a)));
  const beta = Math.acos(clamp(-1, 1, (d2 + b2 - a2) / (2 * dist * b)));
  const seg =
    a2 * (alpha - Math.sin(2 * alpha) / 2) + b2 * (beta - Math.sin(2 * beta) / 2);
  return Math.max(0, seg);
}

function clamp(lo: number, hi: number, x: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

/** Distance between two discs' centres. */
function dist(a: SmearDisc, b: SmearDisc): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/** The fraction of cloud i's disc area covered by its intersection with cloud j, in [0,1]. */
export function pairwiseOverlapFraction(i: SmearDisc, j: SmearDisc): number {
  const ri = Number.isFinite(i.r) ? i.r : 0;
  if (ri <= 0) return 0;
  const areaI = Math.PI * ri * ri;
  if (areaI <= 0) return 0;
  const inter = circleIntersectionArea(ri, Number.isFinite(j.r) ? j.r : 0, dist(i, j));
  return clamp01(inter / areaI);
}

/** Per-cloud OPACITY MULTIPLIER in [floor, 1], one per input disc (same order).
 *  Monotonic-decreasing in overlap: a cloud with no neighbours stays 1; a cloud whose
 *  footprint is ~90% covered recedes to ~0.1; a fully-buried cloud recedes to `floor`.
 *  PURE + total (non-finite / zero-radius discs are treated as no footprint -> 1). */
export function smearOpacityMultipliers(
  discs: readonly SmearDisc[],
  opts: SmearOverlapOptions = {},
): number[] {
  const floor = opts.floor ?? DEFAULT_FLOOR;
  const n = discs.length;
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    const di = discs[i];
    // PROBABILISTIC UNION of neighbour coverage: keep = prod(1 - f_j); coverage = 1 - keep.
    let keep = 1;
    for (let j = 0; j < n; j++) {
      if (j === i) continue;
      const f = pairwiseOverlapFraction(di, discs[j]);
      keep *= 1 - f;
    }
    const coverage = 1 - keep; // in [0,1], monotonic in every f_j
    out[i] = clamp(floor, 1, 1 - coverage);
  }
  return out;
}
